import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const UserDetail = () => {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { fetchWithAuth } = useAuth();

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await fetchWithAuth(`/api/users/${id}`);
      if (!response.ok) throw new Error('Failed to fetch user');
      const data = await response.json();
      setUser(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!user) return <div className="error">User not found</div>;

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Link to="/users" className="link">← Back to Users</Link>
      </div>

      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '24px' }}>
        User Details
      </h1>

      <div className="card">
        <h2 className="card-title">Basic Information</h2>
        <div className="grid grid-2">
          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>ID</label>
            <p style={{ marginTop: '4px' }}>{user.id}</p>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Name</label>
            <p style={{ marginTop: '4px' }}>
              {user.name || user.surname ? `${user.name || ''} ${user.surname || ''}`.trim() : 'Anonymous'}
            </p>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Username</label>
            <p style={{ marginTop: '4px' }}>{user.username || 'N/A'}</p>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Email</label>
            <p style={{ marginTop: '4px' }}>{user.email || 'N/A'}</p>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Gender</label>
            <p style={{ marginTop: '4px' }}>
              {user.gender === 1 ? 'Male' : user.gender === 2 ? 'Female' : 'N/A'}
            </p>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Birthdate</label>
            <p style={{ marginTop: '4px' }}>{user.birthdate || 'N/A'}</p>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Device ID</label>
            <p style={{ marginTop: '4px', fontSize: '12px', wordBreak: 'break-all' }}>
              {user.device_id || 'N/A'}
            </p>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Status</label>
            <p style={{ marginTop: '4px' }}>
              {user.active ? (
                <span className="badge badge-success">Active</span>
              ) : (
                <span className="badge badge-warning">Inactive</span>
              )}
              {user.is_anonymous ? (
                <span className="badge badge-info" style={{ marginLeft: '8px' }}>Anonymous</span>
              ) : null}
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="card-title">Additional Information</h2>
        <div className="grid grid-2">
          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Promo Code</label>
            <p style={{ marginTop: '4px' }}>{user.promo_code || 'N/A'}</p>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Hear About Us</label>
            <p style={{ marginTop: '4px' }}>{user.hear_about_us || 'N/A'}</p>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Apple ID</label>
            <p style={{ marginTop: '4px', fontSize: '12px', wordBreak: 'break-all' }}>
              {user.apple_id || 'N/A'}
            </p>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Created At</label>
            <p style={{ marginTop: '4px' }}>{formatDate(user.created_at)}</p>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Updated At</label>
            <p style={{ marginTop: '4px' }}>{formatDate(user.updated_at)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-2">
        <Link to={`/fitnesses?user_id=${id}`} className="card link" style={{ textAlign: 'center', padding: '32px' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎯</div>
          <div style={{ fontWeight: '600' }}>View Fitness Goals</div>
        </Link>
        <Link to={`/foods?user_id=${id}`} className="card link" style={{ textAlign: 'center', padding: '32px' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>🍽️</div>
          <div style={{ fontWeight: '600' }}>View Food Logs</div>
        </Link>
        <Link to={`/weights?user_id=${id}`} className="card link" style={{ textAlign: 'center', padding: '32px' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚖️</div>
          <div style={{ fontWeight: '600' }}>View Weights</div>
        </Link>
        <Link to={`/activities?user_id=${id}`} className="card link" style={{ textAlign: 'center', padding: '32px' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>🏃</div>
          <div style={{ fontWeight: '600' }}>View Activities</div>
        </Link>
      </div>
    </div>
  );
};

export default UserDetail;


